import React, { useMemo } from 'react'
import { Space, Typography, Select, Checkbox, Divider, Badge } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { REGULAR_EVENT_TYPES, PROJECT_EVENT_COLOR, CRM_EVENT_COLOR } from './constants'

const { Text } = Typography

type User = { id: string | number; firstName: string; lastName: string }

interface Props {
  users: User[]
  selectedTypes: string[]
  setSelectedTypes: React.Dispatch<React.SetStateAction<string[]>>
  selectedUsers: string[]
  setSelectedUsers: React.Dispatch<React.SetStateAction<string[]>>
  search: string
  setSearch: React.Dispatch<React.SetStateAction<string>>
  currentUserId: string
}

const FiltersPanel: React.FC<Props> = ({
  users,
  selectedTypes,
  setSelectedTypes,
  selectedUsers,
  setSelectedUsers,
  search,
  setSearch,
  currentUserId,
}) => {
  const { t } = useTranslation()

  const isAllSelected = useMemo(() => {
    return selectedUsers.length === users.length && users.length > 0
  }, [selectedUsers, users])

  const isMeSelected = useMemo(() => {
    return selectedUsers.includes(currentUserId)
  }, [selectedUsers, currentUserId])

  const handleMeChange = (checked: boolean) => {
    if (checked) {
      // Add current user to selection
      setSelectedUsers(prev => {
        if (prev.includes(currentUserId)) return prev
        return [...prev, currentUserId]
      })
    } else {
      // Remove current user from selection
      setSelectedUsers(prev => prev.filter(id => id !== currentUserId))
    }
  }

  const handleAllChange = (checked: boolean) => {
    if (checked) {
      // When selecting "All", select all users (converting IDs to strings)
      setSelectedUsers(users.map(u => String(u.id)))
    } else {
      // When deselecting "All", clear all selections
      setSelectedUsers([])
    }
  }

  const handleUserSelectChange = (values: string[]) => {
    // Combine dropdown selections with current user if "Me" is checked
    const newSelection = isMeSelected 
      ? [...new Set([...values, currentUserId])]
      : values
    setSelectedUsers(newSelection)
  }

  const userOptions = useMemo(() => {
    return users
      .filter(u => String(u.id) !== currentUserId) // Don't show current user in dropdown
      .sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
      )
      .map(u => ({
        value: String(u.id),
        label: `${u.firstName || 'FirstName'} ${u.lastName || 'LastName'}`,
        searchText: `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase(),
      }))
  }, [users, currentUserId])

  // Filter out current user from selected users for the dropdown
  const dropdownValue = useMemo(() => {
    return selectedUsers.filter(id => id !== currentUserId)
  }, [selectedUsers, currentUserId])

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div className='calendar-notes'>
        <Text strong>{t('EventCategories')}</Text>

        <div style={{ marginTop: 8 }}>
          <Checkbox
            checked={selectedTypes.includes('company')}
            onChange={e =>
              setSelectedTypes(prev =>
                e.target.checked ? [...prev, 'company'] : prev.filter(t => t !== 'company'),
              )
            }
          >
            <Badge color={REGULAR_EVENT_TYPES[0].color} /> {t('Company')}
          </Checkbox>
        </div>

        <div style={{ marginTop: 4 }}>
          <Checkbox
            checked={selectedTypes.includes('project')}
            onChange={e =>
              setSelectedTypes(prev =>
                e.target.checked ? [...prev, 'project'] : prev.filter(t => t !== 'project'),
              )
            }
          >
            <Badge color={PROJECT_EVENT_COLOR} /> {t('Project')}
          </Checkbox>
        </div>

        <div style={{ marginTop: 4 }}>
          <Checkbox
            checked={selectedTypes.includes('crm')}
            onChange={e =>
              setSelectedTypes(prev =>
                e.target.checked ? [...prev, 'crm'] : prev.filter(t => t !== 'crm'),
              )
            }
          >
            <Badge color={CRM_EVENT_COLOR} /> {t('CRM')}
          </Checkbox>
        </div>
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <div>
        <Text strong>{t('User')}</Text>
        
        <div style={{ marginTop: 8 }}>
          <Checkbox
            checked={isMeSelected}
            onChange={e => handleMeChange(e.target.checked)}
          >
            {t('MyEvents')}
          </Checkbox>
        </div>

        <div style={{ marginTop: 4 }}>
          <Checkbox
            checked={isAllSelected}
            onChange={e => handleAllChange(e.target.checked)}
          >
            {t('AllUsers')}
          </Checkbox>
        </div>

        <Select
          mode="multiple"
          style={{ width: '100%', marginTop: 12 }}
          placeholder={t('SelectUsers')}
          value={dropdownValue}
          onChange={handleUserSelectChange}
          allowClear
          showSearch
          filterOption={(input, option) => {
            const opt = userOptions.find(o => o.value === option?.value)
            return opt?.searchText.includes(input.toLowerCase()) ?? false
          }}
          maxTagCount={2}
          maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} ${t('more')}`}
          options={userOptions.map(opt => ({
            value: opt.value,
            label: opt.label,
          }))}
          optionRender={(option) => (
            <Space size="small">
              <UserOutlined />
              {option.label}
            </Space>
          )}
        />

        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {selectedUsers.length} {t('Selected')}
          </Text>
        </div>
      </div>
    </Space>
  )
}

export default FiltersPanel